import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { ModalComponent } from "../../../shared/modal/modal.component";
import { CreateSiteModalComponent } from "../../../shared/create-site-modal/create-site-modal.component";
import { DropdownInputComponent } from "../../../shared/dropdown-input/dropdown-input.component";
import { PatientDetail } from "../../../core/models/patient-detail.model";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { TravelRequestedOptions } from "../../../core/constants/travel-requested-options";
import { CaregiverTravellingOptions } from "../../../core/constants/caregiver-travelling-options";
import { VisitAttendanceOptions } from "../../../core/constants/visit-attendance-options";
import { PatientService } from "../../../core/services/patient.service";
import { AlertService } from "../../../shared/alert/alert.service";
import { TemplateService } from "../../../core/services/template.service";
import { AuthService } from "../../../core/services/auth.service";
import { endDateAfterStartDateValidator } from "../../../core/validators/end-after-start-date.validator";
import { SiteListItem } from "../../../core/models/site-list.model";
import { DateHelper, IllyTime } from "../../../core/helpers/date-helper";
import { PatientAddVisit } from "../../../core/services/interfaces/patient-add-visit.interface";
import { LogHelper } from "../../../core/helpers/log.helper";
import { PatientAutoCompleteComponent } from "../../../shared/patient-autocomplete/patient-autocomplete.component";
import { SelectOption } from "../../../core/models/select-option.model";
import { TrialAutocompleteComponent } from "../../../shared/trial-autocomplete/trial-autocomplete.component";
import { TrialAutocomplete } from "../../../shared/trial-autocomplete/trial-autocomplete.model";
import { ObjectHelper } from "../../../core/helpers/object.helper";
import { BookingStatusOptions } from "../../../core/constants/booking-status-options";
import { PublishedOptions } from "../../../core/constants/published-options";
import { AdminService } from "../../../core/services/admin.service";
import { ComponentBase } from "../../../core/component-base";
import { PatientUpdateAddressComponent } from "../../../shared/patient-update-address/patient-update-address.component";
import { Address } from "../../../shared/input-address/address.model";
import { enumToText } from 'app/core/helpers/enum-to-text.function';
import { StudyPhase } from 'app/core/models/visit-template.model';
import { noWhitespaceValidator, requiredNoWhitespaceValidator } from "../../../core/validators/no-whitespace.validator";

@Component({
  selector: 'app-add-visit-modal',
  templateUrl: './add-visit-modal.component.html',
  styleUrls: ['./add-visit-modal.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AddVisitModalComponent extends ComponentBase implements OnInit, AfterViewInit {
  @ViewChild('modal') modal: ModalComponent;
  @ViewChild('createSiteModal') createSideModal: CreateSiteModalComponent;
  @ViewChild('patientUpdateAddressModal') patientUpdateAddressModal: PatientUpdateAddressComponent;
  @ViewChild('visitType') visitType: DropdownInputComponent;
  @ViewChild('dateInput') dateInput: ElementRef;
  @ViewChild('endDateInput') endDateInput: ElementRef;
  @ViewChild('patientAutoComplete') patientAutoComplete: PatientAutoCompleteComponent;
  @ViewChild('templateDetailsSelect') templateDetailsSelect: DropdownInputComponent;

  @ViewChild('travelRequestedSelect') travelRequestedSelect: DropdownInputComponent;
  @ViewChild('caregiverTravellingSelect') caregiverTravellingSelect: DropdownInputComponent;
  @ViewChild('attendanceSelect') attendanceSelect: DropdownInputComponent;
  @ViewChild('bookingStatusSelect') bookingStatusSelect: DropdownInputComponent;
  @ViewChild('publishedStatusSelect') publishedStatusSelect: DropdownInputComponent;
  @ViewChild('trialAutocomplete') trialAutocomplete: TrialAutocompleteComponent;
  @ViewChild('studyPhaseSelect') studyPhaseSelect: DropdownInputComponent;
  @ViewChild('virtualCallTypeSelect') virtualCallTypeSelect: DropdownInputComponent;

  @Output('addedOrUpdated') addedOrUpdated = new EventEmitter();

  patientTrialId: string;
  isFormProcessing = false;
  patient = new PatientDetail();
  patientLoaded = false;
  form: UntypedFormGroup;
  visitTypeOptions: { value: string, text: string }[] = [];
  travelRequestedOptions = TravelRequestedOptions.all();
  caregiverTravellingOptions = CaregiverTravellingOptions.all();
  visitAttendanceOptions = VisitAttendanceOptions.all();
  bookingStatusOptions = BookingStatusOptions.all();
  publishedOptions = PublishedOptions.all();
  selectedPatientOption: SelectOption;
  coordinatorOptions: SelectOption[] = [];
  enableSelectPatientAndTrial = true;
  templateDetailsOptions: SelectOption[] = [];
  apiEnabledOnTrial: boolean = false;
  virtualCallTypeOptions: SelectOption[] = [
    { value: 'Audio', text: 'Audio Call' },
    { value: 'Video', text: 'Video Call' }
  ];
  studyPhases: SelectOption[] = [
    {
      value: StudyPhase.Treatment.toString(),
      text: enumToText(StudyPhase, StudyPhase.Treatment)
    },
    {
      value: StudyPhase.Screening.toString(),
      text: enumToText(StudyPhase, StudyPhase.Screening)
    }];

  constructor(
    private readonly patientService: PatientService,
    private readonly alertService: AlertService,
    private readonly templateService: TemplateService,
    public authService: AuthService,
    private adminService: AdminService,
    private cd: ChangeDetectorRef) {
    super();
  }

  ngOnInit() {
    this.templateService.showHeader();

    this.updateVisitTypeOptions();

    this.form = new UntypedFormGroup({
      trialId: new UntypedFormControl('', Validators.required),
      templateId: new UntypedFormControl(''),
      date: new UntypedFormControl('', requiredNoWhitespaceValidator()),
      time: new UntypedFormControl(''),
      endDate: new UntypedFormControl('', noWhitespaceValidator()),
      endTime: new UntypedFormControl(''),
      visitTitle: new UntypedFormControl('', requiredNoWhitespaceValidator()),
      description: new UntypedFormControl('', Validators.maxLength(2000)),
      notes: new UntypedFormControl('', Validators.maxLength(2000)),
      visitType: new UntypedFormControl('OnSite', Validators.required),
      department: new UntypedFormControl(''),
      site: new UntypedFormControl(''),
      travelRequested: new UntypedFormControl(),
      caregiverTravelling: new UntypedFormControl(),
      attendance: new UntypedFormControl(),
      patientId: new UntypedFormControl('', Validators.required),
      patientName: new UntypedFormControl(),
      bookingStatus: new UntypedFormControl('NotStarted'),
      publishedStatus: new UntypedFormControl('Draft'),
      designatedContacts: new UntypedFormControl(''),
      travelDays: new UntypedFormControl(),
      nonVisit: new UntypedFormControl(false),
      takeMedicationAtVisit: new UntypedFormControl(false),
      urineSample3DaysBefore: new UntypedFormControl(false),
      urineSample10DaysBefore: new UntypedFormControl(false),
      externalId: new UntypedFormControl('', Validators.maxLength(500)),
      virtualCallType: new UntypedFormControl(null),
      virtualCallTranscriptEnabled: new UntypedFormControl(false)
    }, {
      validators: endDateAfterStartDateValidator,
      updateOn: 'change'
    });
  }

  ngAfterViewInit(): void {
    // Default visit type to on-site (deferred to avoid ExpressionChangedAfterChecked)
    Promise.resolve().then(() => {
      this.visitType.setValue('OnSite');
      if (this.bookingStatusSelect) this.bookingStatusSelect.setValue('NotStarted');
      if (this.publishedStatusSelect) this.publishedStatusSelect.setValue('Draft');
      this.form.markAsPristine();
    });

    this.patientAutoComplete?.selectedChanged.subscribe((option: SelectOption) => {
      this.selectedPatientOption = option;

      if (option === null) {
        return;
      }

      this.trialAutocomplete.setPatientId(option.value);

      // If a trial is already selected (e.g. the patient selection was cleared and re-made), reload the
      // trial-specific patient details so the site/timezone data is refreshed instead of going stale
      const trialId = this.form.get('trialId')?.value;
      if (!ObjectHelper.isUndefinedNullOrEmpty(trialId)) {
        this.loadPatientDetailsForTrial(option.value, trialId);
        this.loadPatientTrialTemplateVisits(option.value, trialId);
      } else {
        this.loadPatientDetails(option.value);
      }
    });

    this.trialAutocomplete.valueChanged.subscribe((option: TrialAutocomplete) => {
      this.form.patchValue({ trialId: option.id });

      // Load patient details for the selected trial
      this.loadPatientDetailsForTrial(this.form.get('patientId')?.value, option.id);

      // Load a list of template visits for the selected patient/trial combination
      this.loadPatientTrialTemplateVisits(this.form.get('patientId')?.value, option.id);
    });

    // When the start date of the visit changes, check if the end date is before the start date and update
    // the visit attendance value to pending confirmation if the start date is in the future
    this.form.get('date').valueChanges.subscribe(value => {
      if (value !== undefined && value !== null && value !== '') {
        let today = new Date();
        let date = DateHelper.stringToDate(value)

        if (this.form.get('attendance').value === null || this.form.get('attendance').value === '') {
          if (date > today) {
            this.form.patchValue({ attendance: 'PendingConfirmation' });
            this.attendanceSelect.setValue('PendingConfirmation');
          } else if (date < today) {
            this.form.patchValue({ attendance: 'Attended' });
            this.attendanceSelect.setValue('Attended');
          }
        }
      }
    });

  }

  loadCoordinators() {
    this.adminService.retrieveAdminUsers(
      1,
      9999,
      "ProjectCoordinatorExpenses,ProjectCoordinatorTravel").subscribe({
        next: results => {
          for (let result of results.results) {
            this.coordinatorOptions.push({ value: result.id, text: result.firstname + ' ' + result.lastname });
          }
        }, error: error => {
          LogHelper.log(error);
          this.alertService.showWarningAlert('There was a problem loading coordinators, please try again!');
        }
      });
  }

  private loadPatientTrialTemplateVisits(patientId: string, trialId: string): void {
    this.templateDetailsOptions = [];
    this.patientService.retrievePatientTemplateVisits(patientId, trialId).subscribe({
      next: templates => {
        templates.forEach(template => {
          this.templateDetailsOptions.push({ value: template.id, text: template.templateText });
        });
      },
      error: err => {
        this.alertService.showWarningAlert(err);
      }
    });
  }

  private loadPatientDetailsForTrial(patientId: string, trialId: string): void {
    this.patientLoaded = false;
    this.patientService.retrievePatientDetailFromPatientIdAndTrialId(patientId, trialId).subscribe({
      next: patient => {
        this.patient = patient;
        this.patientLoaded = true;

        this.form.patchValue({
          patientId: patient.id,
          patientName: patient.firstname + ' ' + patient.lastname
        });

        if (this.patient.trialBrand?.toLowerCase() === 'adarx') {
          this.form.addControl('studyPhase', new UntypedFormControl(null));
          this.form?.get('studyPhase')?.setValidators(Validators.required);

          this.form?.get('studyPhase')?.updateValueAndValidity({ emitEvent: false });
          this.cd.detectChanges();
        } else {
          if (this.form.contains('studyPhase')) {
            this.form.removeControl('studyPhase');
            this.cd.detectChanges();
          }
        }

        this.form.patchValue({ designatedContacts: patient.trialDesignatedContactIds });

        this.apiEnabledOnTrial = patient.apiEnabled;

        this.updateVisitTypeOptions();

        // If the patient has a site, then pre-select it
        if (!ObjectHelper.isUndefinedNullOrEmpty(patient.siteId)) {
          if (patient.siteId) {
            this.form.patchValue({ site: patient.siteId });
          }
        }
      },
      error: (error) => {
        LogHelper.log(error);
        this.alertService.showWarningAlert('Unable to load patient information!');
      }
    });
  }

  get showVirtualCallFields(): boolean {
    return this.form?.get('visitType')?.value === 'Virtual' && this.patient?.virtualCallsEnabled;
  }

  private updateVisitTypeOptions(): void {
    this.visitTypeOptions = [
      { value: 'Home', text: 'Home' },
      { value: 'OnSite', text: 'On-Site' },
      { value: 'Virtual', text: 'Virtual' },
      { value: 'Telephone', text: 'Telephone' },
    ];

  }

  /**
   * Loads the patients details and then loads categories/visits for the patients trial
   * @param patientId
   */
  private loadPatientDetails(patientId: string) {
    this.patientService.retrievePatientDetail(patientId).subscribe({
      next: (patient) => {
        this.patient = patient;

        this.form.patchValue({
          patientId: patient.id,
          patientName: patient.firstname + ' ' + patient.lastname
        });
      },
      error: (error) => {
        LogHelper.log(error);
        this.alertService.showErrorAlert(error);
      }
    });
  }

  show(patientId: string = null, trialId: string = null, options: any = {}): void {
    this.loadCoordinators();
    this.resetForm();
    this.modal.show();

    if (options.hasOwnProperty('enableSelectPatientAndTrial')) {
      this.enableSelectPatientAndTrial = options.enableSelectPatientAndTrial;
    }

    if (patientId !== null) {
      this.form.patchValue({ patientId: patientId })
    }

    if (trialId !== null) {
      this.patient.trialId = trialId
      this.form.patchValue({ trialId: trialId });

      // Load the patient details for the trial
      this.loadPatientDetailsForTrial(patientId, trialId);

      // Load template visits for this patient/trial combination
      this.loadPatientTrialTemplateVisits(patientId, trialId);
    }
  }

  hide(): void {
    this.modal.hide();
  }

  onNewSiteCreated(site: SiteListItem): void {
    this.visitType.setValue('OnSite');
    this.form.patchValue({ site: site.id });
  }

  /**
   * Updates the date in the form when you move your mouse around, a bit of a bodge fix because angular doesn't
   * automatically detect changes
   */
  onMouseEnterForm() {
    if (this.dateInput.nativeElement.value != this.form.get('date')) {
      this.form.patchValue({ date: this.dateInput.nativeElement.value });
    }

    if (this.endDateInput.nativeElement.value != this.form.get('endDate')) {
      this.form.patchValue({ endDate: this.endDateInput.nativeElement.value });
    }
  }

  onVisitTypeChanged(value: string) {
    if (value !== 'OnSite') {
      this.form.controls.site.setValidators(null);
      this.form.controls.site.setValue(null);
    } else {
      this.form.controls.site.setValidators(Validators.required);
    }

    this.form.controls.site.updateValueAndValidity();

    // Clear virtual call fields when not Virtual
    if (value !== 'Virtual') {
      this.form.patchValue({ virtualCallType: null, virtualCallTranscriptEnabled: false });
      if (this.virtualCallTypeSelect) this.virtualCallTypeSelect.reset();
    }

    if (value === 'Home' && (this.patient.address == null || this.patient.address == '')) {
      this.patientUpdateAddressModal.openModal(this.patient.id, 'Missing Address', 'If creating a home visit, you will need to set this patient up with a home address.');
      this.visitType.reset();
    } else {
      this.form.patchValue({ visitType: value });
    }

  }

  /**
   * Called when the patient address is updated - will reload the patient details
   */
  onPatientAddressUpdated(address: Address) {
    if (address === null || !address.hasValue())
      return;

    this.loadPatientDetails(this.form.get('patientId')?.value);

    // Wait a second, and then set the trip type to home
    setTimeout(() => {
      this.form.patchValue({ visitType: 'Home' }); // Assume the user wants to create a home visit
      this.visitType.setValue('Home');
    }, 1000);
  }

  resetForm(): void {
    this.enableSelectPatientAndTrial = true;
    this.patient = new PatientDetail();
    this.patientLoaded = false;

    // Using form.reset() breaks the autocomplete
    this.form.patchValue({
      patientId: '',
      patientName: '',
      trialId: '',
      visitTitle: '',
      date: '',
      time: '',
      endDate: '',
      endTime: '',
      description: '',
      notes: '',
      visitType: 'OnSite',
      site: '',
      travelRequested: '',
      caregiverTravelling: '',
      attendance: '',
      bookingStatus: 'NotStarted',
      publishedStatus: 'Draft',
      designatedContacts: '',
      nonVisit: false,
      takeMedicationAtVisit: false,
      urineSample3DaysBefore: false,
      urineSample10DaysBefore: false,
      externalId: '',
      studyPhase: null,
      virtualCallType: null,
      virtualCallTranscriptEnabled: false
    });

    this.apiEnabledOnTrial = false;
    this.form.get('notes')?.reset();
    this.form.get('description')?.reset();

    if (this.patientAutoComplete)
      this.patientAutoComplete.onClear();

    if (this.trialAutocomplete)
      this.trialAutocomplete.reset();

    if (this.bookingStatusSelect)
      this.bookingStatusSelect.setValue('NotStarted')

    if (this.attendanceSelect)
      this.attendanceSelect.reset();

    if (this.studyPhaseSelect)
      this.studyPhaseSelect.reset();

    if (this.virtualCallTypeSelect)
      this.virtualCallTypeSelect.reset();

    this.visitType.setValue('OnSite');
    this.onVisitTypeChanged('OnSite');
    this.publishedStatusSelect.setValue('Draft');
  }

  onFormSubmit() {
    if (this.form.valid) {
      let time = IllyTime.parseString(this.form.get('time').value);
      let endTime = IllyTime.parseString(this.form.get('endTime').value);

      this.isFormProcessing = true;
      const dto: PatientAddVisit = {
        trialId: this.form.get('trialId').value,
        templateId: this.form.get('templateId').value,
        date: this.form.get('date').value,
        endDate: this.form.get('endDate').value,
        timeMinutes: time.totalMinutes(),
        endTimeMinutes: endTime.totalMinutes(),
        title: this.form.get('visitTitle').value,
        description: this.form.get('description').value,
        notes: this.form.get('notes').value,
        visitType: this.form.get('visitType').value,
        department: this.form.get('department').value,
        siteId: this.form.get('site').value,
        travelRequested: this.form.get('travelRequested').value,
        caregiverTravelling: this.form.get('caregiverTravelling').value,
        attendance: this.form.get('attendance').value,
        bookingStatus: this.form.get('bookingStatus')?.value,
        publishedStatus: this.form.get('publishedStatus')?.value,
        travelDays: this.form.get('travelDays')?.value,
        designatedContacts: this.form.get('designatedContacts')?.value,
        nonVisit: this.form.get('nonVisit')?.value,
        takeMedicationAtVisit: this.form.get('takeMedicationAtVisit')?.value,
        urineSample3DaysBefore: this.form.get('urineSample3DaysBefore')?.value,
        urineSample10DaysBefore: this.form.get('urineSample10DaysBefore')?.value,
        externalId: this.form.get('externalId')?.value,
        studyPhase: this.form.get('studyPhase')?.value,
        virtualCallType: this.form.get('virtualCallType')?.value,
        virtualCallTranscriptEnabled: this.form.get('virtualCallTranscriptEnabled')?.value ?? false
      };

      this.patientService.addVisit(this.form.get('patientId')?.value, dto).subscribe({
        next: () => {
          this.isFormProcessing = false;
          this.alertService.showSuccessAlert('Visit Successfully Added.');
          this.addedOrUpdated.emit();
          this.hide();
        },
        error: error => {
          LogHelper.log(error);
          this.isFormProcessing = false;
          this.alertService.showErrorAlert(error.error);
        }
      });
    }
  }
}
